/**
 * "Yenilikler" (sürüm notları; M17, ADR 0064): ofis paneli ve gemi uygulaması bu listeyi
 * gösterir. Müşterinin anlayacağı sade dille yazılır; commit mesajı, PR numarası, ADR, migration
 * ya da iç terim yazılmaz.
 *
 * Koordinatör her yayında listenin **başına** bir giriş ekler. Kurallar (`whats-new.test.tsx` denetler):
 * - `id` benzersizdir: `YYYY-MM-DD-<kısa-ad>`; yayımlanmış girişin `id`'si değişmez (görüldü
 *   işareti ona bağlıdır).
 * - Liste en yeniden eskiye; `date` aşağı doğru artmaz.
 * - `title` ve `items` hem `en` hem `tr`; iki dilde madde sayısı eşit, madde boş değil.
 * - `scopes`: `office` web ofiste, `ship` gemi uygulamasında görünür; en az biri.
 */

export type WhatsNewScope = 'office' | 'ship';

export interface ReleaseNote {
  /** `YYYY-MM-DD-<kısa-ad>`; görüldü işareti bu kimliği saklar. */
  id: string;
  /** Yayın günü (UTC), `YYYY-MM-DD`. */
  date: string;
  scopes: readonly WhatsNewScope[];
  title: { en: string; tr: string };
  items: { en: readonly string[]; tr: readonly string[] };
}

export const RELEASE_NOTES: readonly ReleaseNote[] = [
  {
    id: '2026-10-20-cash',
    date: '2026-10-20',
    scopes: ['office', 'ship'],
    title: {
      en: 'Finance: master’s cash account and Cash to Master (CTM)',
      tr: 'Finans: kaptan kasası ve kaptana nakit (CTM)',
    },
    items: {
      en: [
        'New Finance menu: Cash to Master (requests, approvals and transfers, receipt confirmation, history), Cash Account (transactions, balance by currency, reconciliation, monthly report) and Finance Reports.',
        'The master requests cash on board; the office approves or returns it and records how it is paid or delivered. The cash balance increases only when the master confirms the cash actually received.',
        'Each currency is a separate cash account. Transactions cannot be changed or deleted: corrections are made with a reversal. Currency exchange records both accounts and the applied rate.',
        'Month-end count: opening, received, paid, calculated closing, cash counted and difference; the master signs, the month is locked and the office reviews. On change of master: cash handover and takeover.',
        'Crew cash advances and cash wages are posted from Payroll once, so the same payment is never entered twice. Only users with the cash permission see the Finance menu.',
      ],
      tr: [
        'Yeni Finans menüsü: Kaptana Nakit (talepler, onay ve transfer, teslim teyidi, geçmiş), Kasa Hesabı (işlemler, para birimine göre bakiye, mutabakat, aylık rapor) ve Finans Raporları.',
        'Kaptan gemide nakit ister; ofis onaylar ya da iade eder ve nasıl ödendiğini ya da teslim edildiğini kaydeder. Kasa bakiyesi yalnız kaptan fiilen teslim aldığını teyit edince artar.',
        'Her para birimi ayrı kasadır. İşlemler değiştirilmez ve silinmez: düzeltme ters kayıtla yapılır. Döviz bozdurma iki kasayı ve uygulanan kuru kaydeder.',
        'Ay sonu sayımı: açılış, giriş, çıkış, hesaplanan kapanış, sayılan nakit ve fark; kaptan imzalar, ay kilitlenir, ofis inceler. Kaptan değişiminde kasa devri ve devralma.',
        'Mürettebat avansı ve nakit maaş Payroll’dan bir kez işlenir; aynı ödeme iki kez girilmez. Finans menüsünü yalnız kasa yetkisi olanlar görür.',
      ],
    },
  },
  {
    id: '2026-10-20-risk-dashboard',
    date: '2026-10-20',
    scopes: ['office', 'ship'],
    title: {
      en: 'Risk assessment: dashboard, reports, PDF and settings',
      tr: 'Risk değerlendirmesi: pano, raporlar, PDF ve ayarlar',
    },
    items: {
      en: [
        'New Compliance ▸ Risk Assessment ▸ Dashboard: current assessments, acknowledgements awaiting or late, reviews due, operations awaiting authorisation, open operations with a High or Critical residual risk and stopped work. In the office the fleet view also shows the company library and the compliance of each vessel.',
        'New Monitoring & Reports: library status, register compliance and verification history, with filters and CSV export.',
        'Print a published risk assessment version from the Vessel Risk Register and an operational verification from its record as PDF, with the approval trail, the answers and the ship time.',
        'New Settings ▸ Risk Assessment: review interval and “due” warning, authorisation validity and the number of days after which an acknowledgement is late. Settings can only make the rules stricter; on board they are read only.',
        'On board, operations awaiting authorisation now appear in the bell for the master and authorised officers.',
      ],
      tr: [
        'Yeni Compliance ▸ Risk Değerlendirmesi ▸ Pano: güncel değerlendirmeler, bekleyen ya da gecikmiş okundu onayları, gözden geçirmesi gelenler, yetki bekleyen operasyonlar, kalan riski High ya da Critical açık operasyonlar ve durdurulan işler. Ofiste filo görünümü şirket kütüphanesini ve her geminin uyumunu da gösterir.',
        'Yeni İzleme ve Raporlar: kütüphane durumu, register uyumu ve doğrulama geçmişi; süzgeç ve CSV dışa aktarma.',
        'Yayımlı risk değerlendirmesi sürümünü Gemi Risk Kaydı’ndan, operasyonel doğrulamayı kendi kaydından onay izi, cevaplar ve gemi saatiyle PDF olarak basın.',
        'Yeni Ayarlar ▸ Risk Değerlendirmesi: gözden geçirme aralığı ve “yaklaşıyor” uyarısı, yetki geçerliliği ve okundu onayının kaç gün sonra gecikmiş sayılacağı. Ayarlar kuralları yalnız sıkılaştırır; gemide salt okunurdur.',
        'Gemide yetki bekleyen operasyonlar artık kaptanın ve yetkili zabitlerin zilinde görünür.',
      ],
    },
  },
  {
    id: '2026-10-20-risk-findings',
    date: '2026-10-20',
    scopes: ['office', 'ship'],
    title: {
      en: 'Risk assessment: findings, corrective actions and company review',
      tr: 'Risk değerlendirmesi: bulgular, düzeltici faaliyetler ve şirket incelemesi',
    },
    items: {
      en: [
        'New Compliance ▸ Risk Assessment ▸ Findings & Actions. A “No” on an operational verification is not automatically a non-conformity: it opens a finding to be corrected, reverified and closed.',
        'Submitting a verification with a “No” opens a blocking finding for each failed check. You can also report a finding on a verification.',
        'Record the immediate action, the responsible person, the due date and the corrective actions; a completed action needs its evidence. The original “No” stays in the history after the reverification.',
        'A blocking finding holds the authorisation until it is closed, and an operation cannot be closed while a finding is open. The master or an authorised officer closes findings.',
        'A new uncontrolled hazard is escalated to the DPA, who approves the safe controls or returns them in the office. A finding can also request a change to the company risk assessment, decided by the library reviewer.',
        'From a finding you can open an HSE Non-Conformity draft or raise a PMS defect; both are linked to the finding.',
      ],
      tr: [
        'Yeni Compliance ▸ Risk Değerlendirmesi ▸ Bulgular ve Faaliyetler. Operasyonel doğrulamadaki “Hayır” kendiliğinden uygunsuzluk değildir: düzeltilip yeniden doğrulanacak ve kapanacak bir bulgu açar.',
        'Doğrulama “Hayır” ile gönderilince karşılanmayan her madde için engelleyici bulgu açılır. Bir doğrulamada elle de bulgu bildirebilirsiniz.',
        'Acil eylemi, sorumluyu, son tarihi ve düzeltici faaliyetleri yazın; tamamlanan faaliyet kanıt ister. Yeniden doğrulamadan sonra ilk “Hayır” geçmişte kalır.',
        'Engelleyici bulgu kapanana dek yetki verilmez; bulgu açıkken operasyon kapanmaz. Bulguları kaptan ya da yetkili zabit kapatır.',
        'Kontrolsüz yeni tehlike DPA’ya yükseltilir; DPA ofiste güvenli kontrolleri onaylar ya da geri gönderir. Bulgudan şirket risk değerlendirmesinde değişiklik de istenebilir; kararı kütüphanenin gözden geçireni verir.',
        'Bulgudan taslak HSE uygunsuzluğu ya da PMS arızası açabilirsiniz; ikisi de bulguya bağlanır.',
      ],
    },
  },
  {
    id: '2026-10-20-risk-verification',
    date: '2026-10-20',
    scopes: ['office', 'ship'],
    title: {
      en: 'Risk assessment: operational verification and authorisation on board',
      tr: 'Risk değerlendirmesi: gemide operasyonel doğrulama ve yetki',
    },
    items: {
      en: [
        'New Compliance ▸ Risk Assessment ▸ Operational Verification: before an operation such as mooring, anchoring or bunkering, verify it against the current company risk assessment assigned to the vessel.',
        'The checklist has six common checks, the activity-specific checks set by the office in the library and every existing control marked mandatory. Answer Yes, No or N/A (N/A only where allowed); No and N/A need a reason.',
        'Six “Yes” answers are not permission to start: the verification goes to Pending Authorization. A No puts it On Hold, a new uncontrolled hazard escalates it, and a reverification keeps the earlier answers.',
        'The master or an authorised officer authorises, returns or rejects; with a High residual risk only the master authorises. The authorisation is valid for at most 24 hours and falls when conditions change.',
        'Anyone on board can stop the work at any time. Crew members acknowledge the briefing with their own login; every decision records the name, role and ship time.',
        'In the library the office can add optional activity-specific checks to an assessment. In the office the verifications are read only.',
      ],
      tr: [
        'Yeni Compliance ▸ Risk Değerlendirmesi ▸ Operasyonel Doğrulama: palamar, demirleme ya da yakıt ikmali gibi bir operasyondan önce onu gemiye atanmış güncel şirket risk değerlendirmesine göre doğrulayın.',
        'Kontrol listesi altı ortak madde, ofisin kütüphanede belirlediği faaliyete özel maddeler ve "zorunlu" işaretli her mevcut kontrolden oluşur. Evet, Hayır ya da N/A ile cevaplayın (N/A yalnız izinli maddede); Hayır ve N/A gerekçe ister.',
        'Altı “Evet” işe başlama izni değildir: doğrulama Yetki Bekliyor olur. Bir Hayır işi Bekletir, kontrolsüz yeni tehlike yükseltilir; yeniden doğrulama önceki cevapları korur.',
        'Kaptan ya da yetkili zabit yetki verir, iade eder ya da reddeder; kalan risk High ise yetkiyi yalnız kaptan verir. Yetki en çok 24 saat geçerlidir ve koşullar değişince düşer.',
        'Gemideki herkes işi her an durdurabilir. Mürettebat brifingi kendi girişiyle onaylar; her karar ad, rol ve gemi saatiyle kaydedilir.',
        'Ofis kütüphanede bir değerlendirmeye isteğe bağlı faaliyete özel maddeler ekleyebilir. Ofiste doğrulamalar salt okunurdur.',
      ],
    },
  },
  {
    id: '2026-10-20-voyage-charter-party',
    date: '2026-10-20',
    scopes: ['office', 'ship'],
    title: {
      en: "Create New Voyage asks for the charter party again; owner's reference for voyages without one",
      tr: 'Yeni sefer çarter sözleşmesini yine soruyor; sözleşmesiz sefer için armatör referansı',
    },
    items: {
      en: [
        'Create New Voyage has a Charter Party section again: choose a charter party from the register and see its terms, or (in the office) enter a new one on the same screen: charterer, CP number, date and type, speed and consumption laden/ballast, weather limits and the other terms.',
        'A new charter party is saved once in the Charter Party Register and the voyage refers to it; the terms are kept in one place.',
        'On board, the charter party of the voyage and its terms are shown read-only when the voyage is created and in Voyage Details.',
        "For voyages without a charter party (spot, owner's voyage, yacht) the analysis uses the owner's reference speed and consumption: a vessel default in Fleet ▸ Vessels, which a voyage may override. Results stay indicative.",
        "Voyage Performance, Fuel & Consumption Analysis and Charter Party Performance show which reference is used: the charter party, the owner's reference or none.",
      ],
      tr: [
        'Yeni Sefer ekranında Çarter Sözleşmesi bölümü geri geldi: kayıttan bir sözleşme seçilir ve şartları görülür ya da (ofiste) aynı ekranda yenisi girilir: kiracı, CP no, tarih ve tür, yüklü/balastlı hız ve tüketim, hava sınırları ve diğer şartlar.',
        'Yeni sözleşme Çarter Sözleşmeleri kaydına bir kez yazılır ve sefer ona bağlanır; şartlar tek yerde tutulur.',
        'Gemide seferin çarter sözleşmesi ve şartları sefer açılırken ve Sefer Ayrıntıları ekranında salt okunur görünür.',
        'Çarter sözleşmesi olmayan seferde (spot, armatör seferi, yat) analiz armatör referansı hız ve tüketimini kullanır: Filo ▸ Gemiler’de gemi varsayılanı, sefer kendi değeriyle değiştirebilir. Sonuçlar gösterge niteliğinde kalır.',
        'Sefer Performansı, Yakıt ve Tüketim Analizi ve Çarter Performansı hangi referansın kullanıldığını gösterir: çarter sözleşmesi, armatör referansı ya da hiçbiri.',
      ],
    },
  },
  {
    id: '2026-10-20-risk-register',
    date: '2026-10-20',
    scopes: ['office', 'ship'],
    title: {
      en: 'Risk assessment: Vessel Risk Register and acknowledgement on board',
      tr: 'Risk değerlendirmesi: Gemi Risk Kaydı ve gemide okundu onayı',
    },
    items: {
      en: [
        'New Compliance ▸ Risk Assessment ▸ Vessel Risk Register: the company risk assessments currently assigned to the vessel, with revision, publication date, assignment, target residual risk and review date.',
        'When the office publishes or assigns a new or revised version, the bell on board shows it until the master or an authorised officer reads and acknowledges it. The acknowledgement records the name, rank and time and cannot be changed.',
        'Versions awaiting acknowledgement are listed first and highlighted; versions migrated from the former templates need no acknowledgement.',
        'Vessel risk assessments that started from an earlier version are listed with “New version available”.',
        'In the office the register is read only: choose a vessel to see its versions and acknowledgements.',
      ],
      tr: [
        'Yeni Compliance ▸ Risk Değerlendirmesi ▸ Gemi Risk Kaydı: gemiye şu an atanmış şirket risk değerlendirmeleri; revizyon, yayım günü, atama, hedef kalan risk ve gözden geçirme günüyle.',
        'Ofis yeni ya da revize bir sürüm yayımlayınca ya da atayınca gemideki zil, kaptan ya da yetkili zabit okuyup onaylayana dek bunu gösterir. Onay ad, rütbe ve saatle kaydedilir; değiştirilemez.',
        'Onay bekleyen sürümler başta ve vurgulu listelenir; eski şablonlardan dönüşen sürümler onay istemez.',
        'Önceki bir sürümden başlamış gemi risk değerlendirmeleri “Yeni sürüm var” ile listelenir.',
        'Ofiste kayıt salt okunurdur: sürümleri ve onayları görmek için gemi seçin.',
      ],
    },
  },
  {
    id: '2026-10-20-own-record-approval',
    date: '2026-10-20',
    scopes: ['office', 'ship'],
    title: {
      en: 'Clear approval status; your own record goes to the office',
      tr: 'Açık onay durumu; kendi kaydınız ofise gider',
    },
    items: {
      en: [
        'When an approver submits their own requisition or defect (for example the Master), it no longer waits on board: under the four-eyes rule the approval goes to the Superintendent in the office.',
        'Requisitions and defects show who the approval is waiting for, on board or in the office, and where the decision is made. The person who submitted sees "You submitted this record; approval goes to …".',
        'The defect report shows the whole flow: Open → Investigating → Repair → Testing → Verification → closure approval (for example Master on board → Superintendent in the office). Office approval comes at closure.',
        'Lists show the awaited approver next to the status, and the office bell counts requisitions waiting for your decision.',
      ],
      tr: [
        'Bir onaylayıcı kendi talebini ya da arızasını gönderdiğinde (ör. kaptan) kayıt artık gemide beklemez: dört göz kuralı gereği onay ofiste Superintendent’a gider.',
        'Talep ve arızalarda onayın kimi beklediği, gemide mi ofiste mi olduğu ve kararın nerede verileceği yazar. Gönderen kişi "Bu kaydı siz gönderdiniz; onay … rolüne gider" notunu görür.',
        'Arıza raporu bütün akışı gösterir: Open → Investigating → Repair → Testing → Verification → kapanış onayı (ör. gemide Master → ofiste Superintendent). Ofis onayı kapanışta gelir.',
        'Listelerde durumun yanında bekleyen onaylayıcı görünür; ofis zili kararınızı bekleyen talepleri sayar.',
      ],
    },
  },
  {
    id: '2026-10-20-test-accounts',
    date: '2026-10-20',
    scopes: ['office', 'ship'],
    title: {
      en: 'Test environment: an account for every rank and office role',
      tr: 'Test ortamı: her rütbe ve ofis rolü için hesap',
    },
    items: {
      en: [
        'In the closed test environment every crew member of the connected demo vessels and every office role can sign in with one shared test password.',
        'The sign-in screen on board and in the office web app lists the test accounts with their rank or role; Use fills in the form. On board it shows the vessel’s crew, in the office the office accounts; the other side is listed for information.',
        'Test accounts sign in to the office without the two-factor step. The list appears only while the test environment is switched on; real companies are never listed.',
      ],
      tr: [
        'Kapalı test ortamında bağlı tanıtım gemilerinin bütün mürettebatı ve her ofis rolü tek ortak test parolasıyla girebilir.',
        'Gemide ve web ofiste giriş ekranı test hesaplarını rütbe ya da rolüyle listeler; Kullan formu doldurur. Gemide o geminin mürettebatı, ofiste ofis hesapları; öbür taraf bilgi için listelenir.',
        'Test hesapları ofise iki adımlı doğrulama olmadan girer. Liste yalnız test ortamı açıkken görünür; gerçek şirketler asla listelenmez.',
      ],
    },
  },
  {
    id: '2026-10-20-risk-library',
    date: '2026-10-20',
    scopes: ['office', 'ship'],
    title: {
      en: 'Risk assessment: company library with review and published versions',
      tr: 'Risk değerlendirmesi: inceleme ve yayımlı sürümlerle şirket kütüphanesi',
    },
    items: {
      en: [
        'New Compliance ▸ Risk Assessment ▸ Library: the office writes each company risk assessment in a seven-tab Builder (General, Hazards, Initial Risk, Existing Controls, Additional Controls, Residual Risk, Approval & Publication).',
        'Each row keeps the inherent, current and target residual risk; selecting an existing control does not mean it is in place, and a draft additional control does not lower the risk.',
        'Assessments go from Draft to Submitted, Under Review and Approved, or back with a reason. A High residual risk needs the DPA’s acceptance before approval; a Critical one is never published.',
        'Publishing creates a fixed version (1.0, 1.1, …) that is assigned to vessel types or vessels. Later changes are made in a new revision; the published version does not change.',
        'On board, the Library lists the published versions; a new vessel risk assessment starts from a current version assigned to the vessel, with the residual risk equal to the initial risk until the controls are verified.',
        'The former risk templates became published version 1.0 for all vessels; they can no longer be edited. Importing the library from Excel now also creates draft assessments from its risk rows.',
      ],
      tr: [
        "Yeni Compliance ▸ Risk Değerlendirmesi ▸ Kütüphane: ofis her şirket risk değerlendirmesini yedi sekmeli Builder'da yazar (Genel, Tehlikeler, İlk Risk, Mevcut Önlemler, Ek Önlemler, Kalan Risk, Onay ve Yayım).",
        'Her satır ilk, güncel ve hedef kalan riski tutar; mevcut önlemi seçmek yerinde olduğu anlamına gelmez, taslak ek önlem riski düşürmez.',
        "Değerlendirme Taslak'tan Gönderildi, İncelemede ve Onaylandı'ya gider ya da gerekçeyle geri döner. High kalan risk onaydan önce DPA kabulü ister; Critical hiç yayımlanmaz.",
        'Yayım, gemi tiplerine ya da gemilere atanan sabit bir sürüm üretir (1.0, 1.1, …). Sonraki değişiklik yeni revizyonla yapılır; yayımlı sürüm değişmez.',
        'Gemide Kütüphane yayımlı sürümleri listeler; yeni gemi risk değerlendirmesi gemiye atanmış güncel bir sürümden başlar ve önlemler doğrulanana dek kalan risk ilk riske eşittir.',
        "Eski risk şablonları bütün gemiler için yayımlı 1.0 sürümü oldu; artık düzenlenmez. Kütüphanenin Excel'den içe aktarılması risk satırlarından taslak değerlendirmeler de oluşturur.",
      ],
    },
  },
  {
    id: '2026-10-20-fuel-analysis',
    date: '2026-10-20',
    scopes: ['office', 'ship'],
    title: {
      en: 'Fuel & Consumption Analysis and voyage cards on the dashboard',
      tr: 'Yakıt ve Tüketim Analizi ve panoda sefer kartları',
    },
    items: {
      en: [
        'New Fuel & Consumption Analysis under Voyage ▸ Performance & Analysis: consumption by fuel type at sea, in port, at anchor, drifting and waiting, the daily average and consumption per mile, for a voyage or for a period.',
        'Consumption is also shown by leg and by period, with a simple chart of how each fuel splits by state.',
        'Bunker Reconciliation compares opening ROB + bunkered − closing ROB with the consumption written in the reports. The ROB Flow lists every report whose ROB is not what the previous ROB, consumption and bunkering give, and draws the ROB trend.',
        'The dashboard shows a card for each voyage in progress: last report and position, next operation, ETA, consumption so far and ROB differences, with links to Voyage Details and the analysis.',
      ],
      tr: [
        'Sefer ▸ Performans ve Analiz altında yeni Yakıt ve Tüketim Analizi: denizde, limanda, demirde, sürüklenmede ve beklemede yakıt türüne göre tüketim, günlük ortalama ve mil başına tüketim; sefer ya da dönem için.',
        'Tüketim bacağa ve döneme göre de gösterilir; her yakıtın durumlara dağılımı basit bir grafikle görünür.',
        'İkmal Mutabakatı, açılış ROB + ikmal − kapanış ROB ile raporlarda yazılan tüketimi karşılaştırır. ROB Akışı, ROB’u önceki ROB, tüketim ve ikmalin verdiğiyle tutmayan her raporu listeler ve ROB eğilimini çizer.',
        'Pano, süren her sefer için bir kart gösterir: son rapor ve konum, sonraki işlem, ETA, şimdiye kadarki tüketim ve ROB farkları; Sefer Ayrıntıları ve analize bağlantılarla.',
      ],
    },
  },
  {
    id: '2026-10-20-charter-party',
    date: '2026-10-20',
    scopes: ['office', 'ship'],
    title: {
      en: 'Charter parties: one register and performance analysis',
      tr: 'Çarter sözleşmeleri: tek liste ve performans analizi',
    },
    items: {
      en: [
        'New Charter Party Register: the office enters the charter terms once (speed, consumption, weather limits, "about" tolerances, exclusions); on board the register is read-only.',
        'The charter period runs from the On Hire to the Off Hire report entered on board; a voyage no longer starts the charter. Charter terms already entered on voyages were moved to the register automatically.',
        "Passages outside any charter are listed separately and the office can classify them as Owner's Account or Awaiting Employment with a reason.",
        'Charter Party Performance now compares actual with contractual speed, consumption and sea time, lists excluded periods and analyses each leg and good weather day.',
        'The office can exclude or include a day or a leg with a reason and mark the analysis as reviewed. The result stays "Indicative" (ship-observed weather) until reviewed and is never labelled contractual.',
        'Fixed: leaving a saved draft report without changes no longer asks to discard unsaved changes.',
      ],
      tr: [
        'Yeni Çarter Sözleşmeleri listesi: ofis çarter şartlarını bir kez girer (hız, tüketim, hava sınırları, "about" toleransları, hariç tutulanlar); gemide liste salt okunurdur.',
        'Çarter dönemi gemide girilen On Hire raporundan Off Hire raporuna kadardır; sefer artık çarteri başlatmaz. Seferlere daha önce girilmiş çarter şartları listeye kendiliğinden taşındı.',
        'Hiçbir çartere girmeyen seyirler ayrıca listelenir; ofis bunları gerekçeyle Armatör Hesabı ya da İş Bekliyor diye sınıflandırabilir.',
        'Çarter Performansı artık gerçekleşen hız, tüketim ve seyir süresini sözleşmeyle karşılaştırır, hariç dönemleri listeler, her bacağı ve iyi hava gününü analiz eder.',
        'Ofis bir günü ya da bacağı gerekçeyle hariç tutabilir ya da dahil edebilir ve analizi incelendi diye işaretleyebilir. Sonuç incelenene kadar "Gösterge niteliğinde" (gemi gözlemi hava) kalır, hiçbir zaman sözleşmesel diye etiketlenmez.',
        'Düzeltildi: kaydedilmiş taslak rapordan değişiklik yapmadan çıkarken artık "kaydedilmemiş değişiklikler" sorulmuyor.',
      ],
    },
  },
  {
    id: '2026-10-20-risk-master-data',
    date: '2026-10-20',
    scopes: ['office', 'ship'],
    title: {
      en: 'Risk assessment: company risk library (master data)',
      tr: 'Risk değerlendirmesi: şirket risk kütüphanesi (ana veriler)',
    },
    items: {
      en: [
        'Compliance ▸ Risk Assessment is now a group with Risk Assessments and Master Data.',
        'Master Data keeps the company risk library: operation areas, activities with task steps, hazards, consequences, existing and additional controls, and the risk rows that link activities to hazards with their initial risk.',
        'In the office, the library is imported from the company Excel workbook. A preview shows new, updated and unchanged records and any errors before anything is written; importing the same file again changes nothing. Records are never deleted: unused ones are retired.',
        'The residual risk of a risk row can stay blank until the controls are verified.',
        'Each vessel gets a risk assessment group (cargo, tanker, yacht / passenger, offshore) in its particulars. On board, a new risk assessment can start from the library: choose the activity and add its suggested hazards and controls.',
      ],
      tr: [
        'Compliance ▸ Risk Assessment artık Risk Değerlendirmeleri ve Ana Veriler’den oluşan bir grup.',
        'Ana Veriler şirketin risk kütüphanesini tutar: operasyon alanları, iş adımlı faaliyetler, tehlikeler, sonuçlar, mevcut ve ek kontroller ve faaliyetleri tehlikelere ilk riskleriyle bağlayan risk satırları.',
        'Ofiste kütüphane şirketin Excel çalışma kitabından içe aktarılır. Önizleme, hiçbir şey yazılmadan önce yeni, güncellenen ve aynı kalan kayıtları ve hataları gösterir; aynı dosya ikinci kez hiçbir şeyi değiştirmez. Kayıt silinmez: kullanılmayan pasifleştirilir.',
        'Risk satırının kalan riski kontroller doğrulanana dek boş kalabilir.',
        'Her gemiye özelliklerinde bir risk değerlendirme grubu verilir (yük, tanker, yat / yolcu, offshore). Gemide yeni risk değerlendirmesi kütüphaneden başlayabilir: faaliyeti seçin, önerilen tehlike ve kontrolleri ekleyin.',
      ],
    },
  },
  {
    id: '2026-10-20-voyage-form-clarity',
    date: '2026-10-20',
    scopes: ['office', 'ship'],
    title: {
      en: 'Voyage reports: clearer errors and Next Operation',
      tr: 'Sefer raporları: daha açık hatalar ve Sonraki İşlem',
    },
    items: {
      en: [
        'A time in the wrong order now says which time it is compared with, e.g. "Must be after Cast Off (18.10.2026 14:20 LT)". An empty required time says "Required".',
        'When a report cannot be saved or submitted, the message counts the fields to fix in each section and the form moves to the first one. An error disappears as soon as you change the field.',
        'In port, Next Operation offers two choices: "Stay in Port" (next: Port Daily Report, one per day) and "Departure". Port Daily reports can follow each other until the departure.',
        'At sea, at anchor and while drifting, the choice reads "Continue Sea Passage", "Stay at Anchor" or "Continue Drifting" with the reports it leads to.',
        'The critical open defect warning on the Departure Report states that it does not block the report.',
      ],
      tr: [
        'Sırası yanlış bir zaman artık hangi zamanla karşılaştırıldığını söyler, ör. "Cast Off (18.10.2026 14:20 LT) zamanından sonra olmalı". Boş zorunlu zaman "Zorunlu" der.',
        'Rapor kaydedilemez ya da gönderilemezse mesaj her bölümde düzeltilecek alan sayısını yazar ve form ilk alana gider. Alanı değiştirince hata hemen kalkar.',
        'Limanda Sonraki İşlem iki seçenek sunar: "Limanda Kal" (sonraki: Limanda Günlük, günde bir) ve "Kalkış". Limanda Günlük raporları kalkışa kadar art arda girilebilir.',
        'Denizde, demirde ve sürüklenirken seçenek "Seyre Devam", "Demirde Kal" ya da "Sürüklenmeye Devam" diye, götürdüğü raporlarla yazılır.',
        'Kalkış Raporundaki kritik açık arıza uyarısı raporu engellemediğini söyler.',
      ],
    },
  },
  {
    id: '2026-10-20-voyage-reports-without-voyage',
    date: '2026-10-20',
    scopes: ['office', 'ship'],
    title: {
      en: 'Voyage: reports without a voyage and linking',
      tr: 'Sefer: sefersiz raporlar ve sefere bağlama',
    },
    items: {
      en: [
        'Every report type can now be entered without a voyage: Quick Create Report offers No Voyage Assigned, Link to Existing Voyage and Create New Voyage for all reports.',
        'The order of reports and the Next Operation now follow the vessel itself, whichever voyage a report belongs to: the vessel is always in one place.',
        'When a voyage is opened, Voyage Details suggests the reports entered without a voyage, in date order and all selected; nothing is linked until you confirm. A linked report can be unlinked again.',
        'One voyage can have several Departure, Noon, Arrival, Anchorage and Drift reports; voyages do not overlap in time. Linking never changes the report itself, and the timeline and summary update at once.',
        'The office can open a voyage and link or unlink reports by request; the vessel applies or rejects the request with a reason, and the office sees the result.',
      ],
      tr: [
        'Artık her rapor türü sefersiz girilebilir: Hızlı Rapor bütün raporlar için Seferi Yok, Var Olan Sefere Bağla ve Yeni Sefer Aç seçeneklerini sunar.',
        'Rapor sırası ve Sonraki İşlem artık rapor hangi seferde olursa olsun geminin kendisini izler: gemi her zaman tek yerdedir.',
        'Sefer açılınca Sefer Ayrıntıları sefersiz girilen raporları tarih sırasıyla ve hepsi seçili önerir; onaylamadan hiçbiri bağlanmaz. Bağlanan rapor yeniden çözülebilir.',
        'Bir sefere birden çok Kalkış, Öğle, Varış, Demir ve Sürüklenme raporu bağlanabilir; seferler zamanda örtüşmez. Bağlamak raporun kendisini değiştirmez; zaman çizelgesi ve özet hemen güncellenir.',
        'Ofis seferi açabilir, raporları talep olarak bağlayıp çözebilir; gemi talebi uygular ya da gerekçeyle reddeder, ofis sonucu görür.',
      ],
    },
  },
  {
    id: '2026-10-19-risk-matrix-profile',
    date: '2026-10-19',
    scopes: ['office', 'ship'],
    title: {
      en: 'Risk assessment: the company sets its own risk matrix',
      tr: 'Risk değerlendirmesi: şirket kendi risk matrisini belirler',
    },
    items: {
      en: [
        'The risk bands are now a company setting. The default is four bands: Low 1–4, Medium 5–9, High 10–16 and Critical 17–25. The earlier five bands remain available as a profile.',
        'In the office, Settings ▸ Risk Matrix shows the bands, who accepts the residual risk in each band and a 5 × 5 preview. A change is published as a new version with a reason; earlier versions are kept.',
        'Every risk assessment submitted from now on keeps the matrix version it was submitted with. Assessments submitted before stay exactly as they were, with their five bands.',
        'A Critical residual risk cannot be submitted. With the four bands, a hazard with a Medium initial risk needs a control measure.',
        'On board, the matrix is read-only and arrives with the next sync.',
      ],
      tr: [
        'Risk bantları artık şirket ayarı. Varsayılan dört bant: Low 1–4, Medium 5–9, High 10–16 ve Critical 17–25. Önceki beş bant profil olarak seçilebilir.',
        'Ofiste Ayarlar ▸ Risk Matrisi bantları, her bantta kalan riski kimin kabul ettiğini ve 5 × 5 önizlemeyi gösterir. Değişiklik gerekçeyle yeni sürüm olarak yayımlanır; eski sürümler saklanır.',
        'Bundan sonra gönderilen her risk değerlendirmesi gönderildiği matris sürümünü taşır. Daha önce gönderilenler beş bantlarıyla olduğu gibi kalır.',
        'Kalan riski Critical olan değerlendirme gönderilemez. Dört bantta ilk riski Medium olan tehlike kontrol önlemi ister.',
        'Gemide matris salt okunur ve sonraki senkronla gelir.',
      ],
    },
  },
  {
    id: '2026-10-19-voyage-timeline-summary',
    date: '2026-10-19',
    scopes: ['office', 'ship'],
    title: {
      en: 'Voyage: details, timeline and summary',
      tr: 'Sefer: ayrıntılar, zaman çizelgesi ve özet',
    },
    items: {
      en: [
        'Voyage Details shows the voyage record with its linked reports in date order, events, legs, fuel consumption and voyage periods.',
        'Voyage Timeline draws the voyage on a horizontal line with events, legs and distances, port and anchorage times, with filters, Fit View and full screen.',
        'Voyage Summary totals sailing, port, anchorage, drift and waiting time, distance and consumption by fuel type, and downloads as PDF: Final when the voyage is completed.',
        'All Reports now shows Vessel Status, Active Voyage, Recent Events, Reports by Type and the voyage timeline; on board, Quick Create Report opens the report forms.',
        'Event Types and Report Templates list the events and the fields of each report type. Everything is derived from the reports; nothing is entered twice.',
      ],
      tr: [
        'Sefer Ayrıntıları, sefer kaydını bağlı raporlarıyla tarih sırasıyla, olaylar, bacaklar, yakıt tüketimi ve sefer dönemleriyle gösterir.',
        'Sefer Zaman Çizelgesi seferi olaylar, bacaklar ve mesafeler, liman ve demir süreleriyle yatay bir çizgide gösterir; süzgeçler, Sığdır ve tam ekranla.',
        'Sefer Özeti seyir, liman, demir, sürüklenme ve bekleme süresini, mesafeyi ve yakıt türüne göre tüketimi toplar ve PDF olarak indirilir: sefer tamamlanınca Nihai.',
        'Tüm Raporlar artık Gemi Durumu, Etkin Sefer, Son Olaylar, Türe Göre Raporlar ve sefer zaman çizelgesini gösterir; gemide Hızlı Rapor rapor formlarını açar.',
        'Olay Türleri ve Rapor Şablonları olayları ve her rapor türünün alanlarını listeler. Hepsi raporlardan türetilir; hiçbir şey iki kez girilmez.',
      ],
    },
  },
  {
    id: '2026-10-19-voyage-all-reports',
    date: '2026-10-19',
    scopes: ['office', 'ship'],
    title: { en: 'Voyage: All Reports', tr: 'Sefer: Tüm Raporlar' },
    items: {
      en: [
        'The Voyage menu is now grouped as Reports, Voyage Reports, Performance & Analysis and Reference Data.',
        'All Reports lists every voyage report of the vessel in chronological order, with or without a voyage, with filters for date, report type, voyage (including Unassigned), port / location and status.',
        'Each report in the menu (Arrival, Daily Port, Departure, Anchoring, Anchor Aweigh, Drifting, Drift Departure, Canal / Strait Transit, Noon) opens All Reports for that report type.',
        'Key Information shows the main values of each report at a glance, and Export saves the filtered list as a CSV file.',
        'On board, Add New Report opens the usual report forms. Voyage Details, Timeline, Summary, Fuel & Consumption Analysis, Report Templates and Event Types are being prepared.',
      ],
      tr: [
        'Sefer menüsü artık Raporlar, Sefer Raporları, Performans ve Analiz ve Referans Veriler diye gruplu.',
        'Tüm Raporlar, geminin bütün sefer raporlarını seferli ya da sefersiz kronolojik sırayla listeler; tarih, rapor türü, sefer (Sefersiz dahil), liman / konum ve durum süzgeçleriyle.',
        'Menüdeki her rapor (Varış, Günlük Liman, Kalkış, Demirleme, Demir Alma, Sürüklenme, Sürüklenme Bitiş, Kanal / Boğaz Geçişi, Öğle) Tüm Raporlar’ı o rapor türüyle açar.',
        'Temel Bilgi her raporun ana değerlerini bir bakışta gösterir; Dışa Aktar süzülmüş listeyi CSV dosyası olarak kaydeder.',
        'Gemide Yeni Rapor, bilinen rapor formlarını açar. Sefer Ayrıntıları, Zaman Çizelgesi, Özet, Yakıt ve Tüketim Analizi, Rapor Şablonları ve Olay Türleri hazırlanıyor.',
      ],
    },
  },
  {
    id: '2026-10-18-documents-company-only',
    date: '2026-10-18',
    scopes: ['office', 'ship'],
    title: {
      en: 'Documents: only the company revises and publishes',
      tr: 'Dokümanlar: yalnız şirket revize eder ve yayınlar',
    },
    items: {
      en: [
        'ISM forms, manuals and procedures are added, revised and published only by the company. On board there is no New Document and no new revision; the vessel reads documents, acknowledges them and fills in forms.',
        'In the office, ISM Forms and Manuals now open on Company documents. Select a document and choose New revision; after the DPA approves it, Publish to vessels sends it to the vessels.',
        'A vessel’s own manual, such as its stability booklet, is published by the office with Distribution: Selected vessels.',
        'Documents a vessel added earlier stay visible and read only. The Master can still return or withdraw such a document once the company has published its version.',
      ],
      tr: [
        'ISM formlarını, el kitaplarını ve prosedürleri yalnız şirket ekler, revize eder ve yayınlar. Gemide Yeni Doküman ve yeni revizyon yoktur; gemi dokümanları okur, teyit eder ve formları doldurur.',
        'Ofiste ISM Forms ve Manuals artık Şirket dokümanları ile açılır. Dokümanı seçip Yeni revizyon’u kullanın; DPA onayladıktan sonra Gemilere yayınla dokümanı gemilere gönderir.',
        'Geminin kendi el kitabını (ör. stabilite kitapçığı) ofis Dağıtım: Seçili gemiler ile yayınlar.',
        'Geminin daha önce eklediği dokümanlar salt okunur görünür kalır. Şirket kendi sürümünü yayınlayınca kaptan böyle bir dokümanı iade edebilir ya da geri çekebilir.',
      ],
    },
  },
  {
    id: '2026-10-18-payroll-deductions',
    date: '2026-10-18',
    scopes: ['office', 'ship'],
    title: {
      en: 'Payroll: deductions such as internet and phone',
      tr: 'Payroll: internet, telefon gibi kesintiler',
    },
    items: {
      en: [
        'On board, Advances & Deductions now takes deductions as well as cash advances and slopchest: internet / Wi-Fi data, phone / calling card, union dues, personal expenses paid by the ship, lost or damaged company property and other deductions.',
        'The company can add its own deduction types in the office (Advances & Deductions ▸ Deduction Types), with an English and a Turkish name. Types are sent to all vessels. A type is never deleted: deactivate it and it is no longer offered, while earlier records keep its name.',
        'Deductions appear in the Other Deductions column of Monthly Payroll; click the amount to see them one by one. The pay slip lists every deduction on its own line with its date and description.',
        'Cash advances and slopchest stay as before, with their own columns.',
      ],
      tr: [
        'Gemide Avans ve Kesintiler artık nakit avans ve slopchest’in yanında kesinti de alır: internet / Wi-Fi veri paketi, telefon / arama kartı, sendika aidatı, geminin ödediği kişisel masraf, kayıp ya da hasarlı şirket malı ve diğer kesintiler.',
        'Şirket kendi kesinti türlerini ofiste İngilizce ve Türkçe adıyla ekleyebilir (Avans ve Kesintiler ▸ Kesinti Türleri). Türler bütün gemilere gider. Tür silinmez: pasifleştirilen tür artık seçilmez, eski kayıtlarda adı kalır.',
        'Kesintiler Aylık Hesap’ta Diğer Kesinti sütununda görünür; tutara tıklayınca tek tek listelenir. Ücret pusulasında her kesinti tarihi ve açıklamasıyla ayrı satırdadır.',
        'Nakit avans ve slopchest eskisi gibi kendi sütunlarında kalır.',
      ],
    },
  },
  {
    id: '2026-10-18-demo-connected',
    date: '2026-10-18',
    scopes: ['ship'],
    title: {
      en: 'Demo connected to the office',
      tr: 'Ofise bağlı demo',
    },
    items: {
      en: [
        'Try the demo can now connect to the office demo company: what you enter on the demo ship, such as a material requisition, appears in the office, and office decisions come back to the ship.',
        'Connected to office is the default when the office can be reached. Offline sample data works as before, without an internet connection.',
        'All demo accounts on the connected ship use the same demo password shown on the sign-in screen.',
        'If the same ship is connected on another computer, the demo asks before taking it over. A computer whose ship was taken over shows This ship moved to another computer, with Reconnect.',
        'In the connected demo, Reset demo downloads the ship again from the office.',
      ],
      tr: [
        'Demoyu dene artık ofisin tanıtım şirketine bağlanabilir: demo gemisinde girdiğiniz malzeme talebi gibi kayıtlar ofiste görünür, ofisin kararı gemiye döner.',
        'Ofise ulaşılabiliyorsa varsayılan seçenek Ofise bağlıdır. Çevrimdışı örnek veri eskisi gibi, internetsiz çalışır.',
        'Bağlı gemideki bütün demo hesapları giriş ekranında yazan aynı demo parolasıyla açılır.',
        'Aynı gemi başka bir bilgisayarda bağlıysa demo devralmadan önce sorar. Gemisi devralınan bilgisayar Bu gemi başka bir bilgisayara geçti der ve Yeniden bağlan sunar.',
        'Bağlı demoda Demoyu sıfırla gemiyi ofisten yeniden indirir.',
      ],
    },
  },
  {
    id: '2026-10-18-demo-keeps-data',
    date: '2026-10-18',
    scopes: ['ship'],
    title: {
      en: 'Demo: your records are kept after an update',
      tr: 'Demo: güncellemeden sonra kayıtlarınız kalır',
    },
    items: {
      en: [
        'Updating the app no longer resets the demo. Records you entered in the demo, such as requisitions or voyages, stay as they are.',
        'When the update brings new sample content, the demo bar says so. To load it, choose Reset demo; the records you entered are then deleted.',
        'Reset demo has a new option, Delete all data: the demo starts clean with the ship, crew and demo accounts, equipment and job plans, ISM form templates and the library, but without any records.',
        'Delete all data asks for confirmation first and only affects the demo on this computer. Real ship data is not affected.',
      ],
      tr: [
        'Uygulamayı güncellemek demoyu artık sıfırlamaz. Demoda girdiğiniz malzeme talebi, sefer gibi kayıtlar olduğu gibi kalır.',
        'Güncelleme yeni örnek içerik getirdiğinde demo şeridi bunu söyler. Yüklemek için Demoyu sıfırla seçilir; girdiğiniz kayıtlar o zaman silinir.',
        'Demoyu sıfırla yeni bir seçenek sunar, Tüm verileri sil: demo; gemi, mürettebat ve demo hesapları, ekipman ve iş planı, ISM form şablonları ve kütüphaneyle, ama hiç kayıt olmadan temiz başlar.',
        'Tüm verileri sil önce onay ister ve yalnız bu bilgisayardaki demoyu etkiler. Gerçek gemi verisi etkilenmez.',
      ],
    },
  },
  {
    id: '2026-10-18-voyage-edit-delete',
    date: '2026-10-18',
    scopes: ['office', 'ship'],
    title: {
      en: 'Edit and delete voyage reports',
      tr: 'Sefer raporunu düzeltme ve silme',
    },
    items: {
      en: [
        'A draft voyage report (departure, arrival, noon or daily) can be deleted with Delete Draft.',
        'A submitted report is corrected with Edit (reopen): the master gives a reason, corrects the report and submits it again as a new revision. The earlier version stays in the history.',
        'A submitted report that should not count can be voided with Void Report, giving a reason. It stays in the history marked Void and no longer counts in the voyage figures.',
        'Only the latest report of a voyage can be deleted or voided, so the report sequence always stays correct.',
        'A voyage without reports can be deleted on the Edit Voyage page. A voyage with reports is voided with a reason instead.',
        'Demo: Reset demo now offers Reset with empty voyages, which keeps the sample crew, maintenance and forms but starts with no voyages, so you can create a voyage from scratch.',
      ],
      tr: [
        'Taslak sefer raporu (kalkış, varış, öğle ya da günlük) Taslağı Sil ile silinebilir.',
        'Gönderilmiş rapor Düzelt (yeniden aç) ile düzeltilir: kaptan gerekçeyle açar, raporu düzeltip yeni revizyon olarak yeniden gönderir. Önceki sürüm geçmişte kalır.',
        'Sayılmaması gereken gönderilmiş rapor, gerekçeyle Raporu İptal Et ile iptal edilebilir. Geçmişte İptal olarak kalır, sefer rakamlarına artık girmez.',
        'Yalnız seferin son raporu silinebilir ya da iptal edilebilir; böylece rapor sırası hep doğru kalır.',
        'Raporu olmayan sefer Seferi Düzenle sayfasında silinebilir. Raporu olan sefer bunun yerine gerekçeyle iptal edilir.',
        'Demo: Demoyu sıfırla artık Seferleri boş başlat seçeneğini de sunar; örnek personel, bakım ve formlar kalır, seferler boş başlar ve sıfırdan sefer oluşturabilirsiniz.',
      ],
    },
  },
  {
    id: '2026-10-18-crew-contract',
    date: '2026-10-18',
    scopes: ['office', 'ship'],
    title: {
      en: 'Contract end dates and the MLC 12-month limit',
      tr: 'Kontrat bitişi ve MLC 12 ay sınırı',
    },
    items: {
      en: [
        'The office can enter a contract end date for each vessel assignment. The form shows the last day allowed on board under the MLC 12-month limit.',
        'A contract ending after the MLC limit can be saved only with a reason, for example a flag State dispensation.',
        'The Crew List shows the contract end and the days left, in the office and on board.',
        'The dashboard and the bell warn 60 days before the contract ends or the MLC limit is reached, whichever comes first, and again once the date has passed.',
      ],
      tr: [
        'Ofis her gemi atamasına kontrat bitiş tarihi girebilir. Form, MLC 12 ay sınırına göre gemide kalınabilecek son günü gösterir.',
        'MLC sınırından sonra biten kontrat yalnız gerekçeyle kaydedilir (ör. bayrak devleti izni).',
        'Personel listesi kontrat bitişini ve kalan günü ofiste ve gemide gösterir.',
        'Pano ve zil, kontrat bitişine ya da MLC sınırına (hangisi önceyse) 60 gün kala ve tarih geçince uyarır.',
      ],
    },
  },
  {
    id: '2026-10-17-approve-without-password',
    date: '2026-10-17',
    scopes: ['office', 'ship'],
    title: {
      en: 'Approve without typing your password again',
      tr: 'Parolayı yeniden yazmadan onay',
    },
    items: {
      en: [
        'You sign in with your own password; after that you approve, sign and submit with the approve button. This includes PMS work records, postponements, defects, rest hours, requisitions, purchase orders, voyage reports, the ship’s clock and payroll.',
        'Each approval still records who approved, with which rank and when, and the content it approved.',
        'If your company wants the password on every approval, an administrator can turn on “Ask for the password on every approval” under Settings ▸ Approvals. Vessels receive the setting with the next sync.',
      ],
      tr: [
        'Kendi parolanızla giriş yaparsınız; sonra onay düğmesiyle onaylar, imzalar ve gönderirsiniz. PMS iş kayıtları, ertelemeler, arızalar, dinlenme saatleri, malzeme talepleri, satın alma siparişleri, sefer raporları, gemi saati ve bordro buna dahildir.',
        'Her onay kimin, hangi rütbeyle ve ne zaman onayladığını ve onaylanan içeriği kaydetmeye devam eder.',
        'Şirketiniz her onayda parola istiyorsa yönetici Ayarlar ▸ Onaylar altında “Onayda parolayı her yerde iste” ayarını açabilir. Ayar gemilere sonraki senkronla iner.',
      ],
    },
  },
  {
    id: '2026-10-17-ism-forms-master-closes',
    date: '2026-10-17',
    scopes: ['office', 'ship'],
    title: {
      en: 'ISM forms: the Master approves, the office sees',
      tr: 'ISM formları: kaptan onaylar, ofis görür',
    },
    items: {
      en: [
        'ISM form records no longer go to the office for approval. The Master approves on board and the record is closed; the office sees every record. A form the Master submits is closed on submission.',
        'Records that were already waiting for an office approval can still be approved in the office by the role they are waiting for. The record page says who that is.',
        'In the office, a record waiting for the Master now says that it is approved on board.',
        'Only the company publishes ISM forms. On board, ISM Forms no longer has “New Document”. Forms the vessel added earlier stay visible. The vessel’s own manuals are not affected.',
        'Who approves a form’s records: select the form in the office. The “Approval chain” card at the top right shows the chain, and “Edit approval chain” changes it. Without a chain, the Master approves.',
      ],
      tr: [
        'ISM form kayıtları artık ofis onayına gitmez. Kaptan gemide onaylar ve kayıt kapanır; ofis her kaydı görür. Kaptanın gönderdiği form gönderimiyle kapanır.',
        'Ofis onayı bekleyen kayıtlar, bekledikleri roldeki kişi tarafından ofiste yine onaylanabilir. Kayıt sayfası bu kişinin kim olduğunu yazar.',
        'Ofiste kaptan onayı bekleyen kayıt artık gemide onaylanacağını yazar.',
        'ISM formlarını yalnız şirket yayınlar. Gemide ISM Forms’ta artık “Yeni Doküman” yok. Geminin daha önce eklediği formlar görünür kalır. Geminin kendi el kitapları bundan etkilenmez.',
        'Bir formun kayıtlarını kim onaylar: ofiste formu seçin. Sağ üstteki “Onay zinciri” kartı zinciri gösterir, “Onay zincirini düzenle” değiştirir. Zincir yoksa kaptan onaylar.',
      ],
    },
  },
  {
    id: '2026-10-17-ism-forms-simpler',
    date: '2026-10-17',
    scopes: ['ship'],
    title: {
      en: 'Simpler ISM form pages on board',
      tr: 'Gemide daha sade ISM form sayfaları',
    },
    items: {
      en: [
        'Each form now has one main button at the top: Fill in for Word and Excel forms, Print for PDF forms, with Upload signed form next to it.',
        'On the right, one card explains how to fill in the form in three steps. Filling a Word or Excel form on paper is still possible, under "Filling it on paper?".',
        'The record page shows three short steps and a status line, for example "Filled file attached from Office ✓". Submit stays at the bottom of the page.',
        'While the form is open in Office, the record page says so and offers Bring Office to front. When you close Office, the filled file is attached and Felixmate comes back to the front.',
        'If a file filled in Office is already attached, uploading a paper scan asks first, so the same form is not added twice.',
      ],
      tr: [
        'Her formun üstünde artık tek ana düğme var: Word ve Excel formunda Formu doldur, PDF formunda Yazdır ve yanında İmzalı formu yükle.',
        'Sağdaki tek kart formun nasıl doldurulacağını üç adımda anlatır. Word ya da Excel formunu kâğıtta doldurmak "Kâğıtta mı dolduruyorsunuz?" altında yine mümkündür.',
        'Kayıt sayfası üç kısa adım ve bir durum satırı gösterir, örneğin "Doldurulan dosya Office’ten eklendi ✓". Gönder düğmesi sayfanın altında kalır.',
        'Form Office’te açıkken kayıt sayfası bunu yazar ve Office’i öne getir düğmesini sunar. Office’i kapatınca doldurulan dosya eklenir ve Felixmate yeniden öne gelir.',
        'Office’te doldurulmuş dosya zaten ekliyse kâğıt taraması yüklemeden önce sorulur; aynı form iki kez eklenmez.',
      ],
    },
  },
  {
    id: '2026-10-17-approval-chain',
    date: '2026-10-17',
    scopes: ['office', 'ship'],
    title: {
      en: 'Approval chains for ISM forms, by rank',
      tr: 'ISM formlarında rütbeye göre onay zinciri',
    },
    items: {
      en: [
        'New ranks: 2nd, 3rd and 4th Officer, 2nd, 3rd and 4th Engineer and Electro-Technical Officer. A person’s rank is the rank of their assignment on the vessel.',
        'When uploading or editing a form, the office can set its approval chain: who prepares it (one or two ranks), up to two reviews in order, the Master and an optional office approval. Where a step has two ranks, choose whether all sign or any one is enough.',
        'Forms without a chain keep the default approval rules. A changed chain applies to records submitted after the change.',
        'The Master can approve only after the reviews; until then the record shows who it is waiting for and the dashboard lists it under Upcoming.',
        'The record page and the record PDF list everyone who signed, with rank, name and time, starting with the person who prepared and submitted the form.',
        'The Pending approvals card appears on the dashboard only for people who approve in a chain or in the approval rules, or who have a record waiting for them.',
      ],
      tr: [
        'Yeni rütbeler: 2., 3. ve 4. zabit, 2., 3. ve 4. mühendis ve elektro-teknik zabit. Kişinin rütbesi gemideki görev atamasının rütbesidir.',
        'Ofis formu yüklerken ya da düzenlerken onay zincirini seçebilir: kim hazırlar (bir ya da iki rütbe), sıralı en çok iki ön onay, kaptan ve isteğe bağlı ofis onayı. İki rütbeli adımda hepsinin mi imzalayacağı yoksa birinin yeterli mi olduğu seçilir.',
        'Zinciri seçilmemiş formlar hazır onay kurallarıyla sürer. Değişen zincir, değişiklikten sonra gönderilen kayıtlara uygulanır.',
        'Kaptan ön onaylar bitmeden onaylayamaz; o zamana kadar kayıt kimi beklediğini gösterir ve pano onu Sırada satırında listeler.',
        'Kayıt sayfası ve kayıt PDF’i imzalayan herkesi rütbe, ad ve zamanla listeler; ilk satır formu hazırlayıp gönderen kişidir.',
        'Pano, Bekleyen onaylar kartını yalnız bir zincirde ya da onay kurallarında onay veren kişiye, ya da kendisini bekleyen kaydı olana gösterir.',
      ],
    },
  },
  {
    id: '2026-10-17-ism-tree-records',
    date: '2026-10-17',
    scopes: ['office', 'ship'],
    title: {
      en: 'Form records in the ISM tree',
      tr: 'ISM ağacında form kayıtları',
    },
    items: {
      en: [
        'Open a form in the ISM tree with its arrow: below it are the vessel’s last five records and every record still awaiting approval, newest first.',
        'Each record shows its number, day, revision and status, for example “⏳ Waiting for Master”. Select it to open the record; “All (n)” opens the full list of the form.',
        'In the office, choose the vessel above the tree. The choice is remembered on this computer; without a vessel the form shows “Select a vessel to see records”.',
      ],
      tr: [
        'ISM ağacında bir formu okuyla açın: altında geminin son beş kaydı ve onay bekleyen bütün kayıtlar yeniden eskiye görünür.',
        'Her kayıt numarasını, gününü, revizyonunu ve durumunu gösterir, örneğin “⏳ Kaptan onayı bekleniyor”. Kaydı açmak için seçin; “Tümü (n)” formun bütün kayıt listesini açar.',
        'Ofiste gemiyi ağacın üstünden seçin. Seçim bu bilgisayarda hatırlanır; gemi seçilmeden formun altında “Kayıtları görmek için gemi seçin” yazar.',
      ],
    },
  },
  {
    id: '2026-10-17-office-pack',
    date: '2026-10-17',
    scopes: ['ship'],
    title: {
      en: 'Felixmate Office Pack: fill in Word and Excel forms inside Felix',
      tr: 'Felixmate Office Pack: Word ve Excel formlarını Felix içinde doldurun',
    },
    items: {
      en: [
        'Felixmate Office Pack is an optional download of about 140 MB. Felix asks once at first start; the pack downloads in the background and continues where it left off if the connection drops.',
        'With the pack installed, “Fill in” and “Open in Office” open Word and Excel forms in Felixmate Office Pack. Save and close the form: it is added to the record together with a PDF copy.',
        'If you close the form without saving, nothing is added. Without the pack, forms open with the program installed on the computer, as before.',
        'Settings shows the pack’s status, version and size, with Download, Pause and Remove.',
      ],
      tr: [
        'Felixmate Office Pack yaklaşık 140 MB’lık isteğe bağlı bir indirmedir. Felix ilk açılışta bir kez sorar; paket arka planda iner, bağlantı koparsa kaldığı yerden sürer.',
        'Paket kuruluysa “Formu doldur” ve “Office’te aç” Word ve Excel formlarını Felixmate Office Pack’te açar. Formu kaydedip kapatın: form PDF kopyasıyla birlikte kayda eklenir.',
        'Formu kaydetmeden kapatırsanız bir şey eklenmez. Paket yoksa formlar eskisi gibi bilgisayarda kurulu programla açılır.',
        'Ayarlar paketin durumunu, sürümünü ve boyutunu gösterir; İndir, Duraklat ve Kaldır buradadır.',
      ],
    },
  },
  {
    id: '2026-10-17-ism-tree-approvals',
    date: '2026-10-17',
    scopes: ['office', 'ship'],
    title: {
      en: 'ISM tree revisions and approval steps',
      tr: 'ISM ağacında revizyonlar ve onay adımları',
    },
    items: {
      en: [
        'Each document and form in the ISM and manual tree shows its revision in force, for example Rev.3. A new revision shows Rev.4 ⏳ while it awaits approval and Rev.4 draft while it is a draft.',
        'Documents in a folder can be sorted by Order or by Date (newest first); your choice is remembered.',
        'A form record shows its approval steps with role, person, decision and time; the next step is highlighted.',
        'An approver whose turn has not come sees Approve disabled with the reason, for example Waiting for Chief Engineer. The dashboard lists these records in grey under Upcoming.',
        'The record PDF ends with a signature block for each approval step. Amending a submitted record warns that approvals already given will be cleared.',
      ],
      tr: [
        'ISM ve el kitabı ağacında her doküman ve form yürürlükteki revizyonunu gösterir, örneğin Rev.3. Yeni revizyon onay beklerken Rev.4 ⏳, taslakken Rev.4 taslak yazar.',
        'Klasördeki dokümanlar Sıra ya da Tarih (yeniden eskiye) ile sıralanır; seçiminiz hatırlanır.',
        'Form kaydı onay adımlarını rol, kişi, karar ve zamanla gösterir; sıradaki adım vurgulanır.',
        'Sırası gelmemiş onaycı Approve düğmesini pasif ve nedeniyle görür, örneğin Başmühendis onayı bekleniyor. Pano bu kayıtları gri Sırada satırlarında listeler.',
        'Kayıt PDF’inin sonunda her onay adımı için imza bloğu vardır. Gönderilmiş kaydı değiştirirken verilmiş onayların düşeceği uyarısı çıkar.',
      ],
    },
  },
  {
    id: '2026-10-17-payroll-extras',
    date: '2026-10-17',
    scopes: ['office', 'ship'],
    title: { en: 'Payroll: itemised extras', tr: 'Payroll: kalem kalem ekstralar' },
    items: {
      en: [
        'A crew member can now have several extras in a month, each with a type, description and amount: overtime, bonus, hold cleaning, lashing, allowances and more. The company can add its own extra types in the office.',
        'Monthly Payroll always shows the Extra column; click the amount to see the extras one by one. The pay slip lists every extra on its own line.',
        'Payroll records now open in a window in the middle of the screen instead of a narrow side panel.',
        'The Daily Work/Rest Record screen no longer shows Active Alerts; certificate alerts stay on the Certificates screen.',
      ],
      tr: [
        'Bir kişinin ayda birden fazla ekstrası olabilir; her biri tür, açıklama ve tutarla: fazla mesai, prim, ambar temizliği, lashing, harcırahlar ve diğerleri. Şirket kendi ekstra türlerini ofiste ekleyebilir.',
        'Aylık Hesap’ta Ekstra sütunu her zaman görünür; tutara tıklayınca ekstralar tek tek listelenir. Ücret pusulasında her ekstra ayrı satırdadır.',
        'Payroll kayıtları dar yan panel yerine ekranın ortasında açılan pencerede düzenlenir.',
        'Günlük Çalışma/Dinlenme Kaydı ekranında Active Alerts bölümü kalktı; sertifika uyarıları Certificates ekranında gösterilir.',
      ],
    },
  },
  {
    id: '2026-10-17-office-admin-access',
    date: '2026-10-17',
    scopes: ['office'],
    title: {
      en: 'Company administrator sees every office page',
      tr: 'Şirket yöneticisi ofisteki her sayfayı görür',
    },
    items: {
      en: [
        'The company administrator can now open every office page: crew, rest hours, voyages, documents, ISM forms, PMS, HSE and more.',
        'The company administrator can edit the document folder tree and upload new document and form revisions; the DPA still approves them.',
        'The menu shows only the pages you may view. A page you may not view now says so instead of offering Try again.',
        'The dashboard shows only the cards for your role; if it has none, the first page you may view opens after sign-in.',
      ],
      tr: [
        'Şirket yöneticisi artık ofisteki her sayfayı açar: personel, dinlenme saatleri, seferler, dokümanlar, ISM formları, PMS, HSE ve diğerleri.',
        'Şirket yöneticisi doküman klasör ağacını düzenler, yeni doküman ve form revizyonu yükler; onayı yine DPA verir.',
        'Menü yalnız görebileceğiniz sayfaları gösterir. Göremeyeceğiniz sayfa Try again yerine bunu söyler.',
        'Pano yalnız rolünüzün kartlarını gösterir; hiç kart yoksa girişten sonra görebildiğiniz ilk sayfa açılır.',
      ],
    },
  },
  {
    id: '2026-10-16-ism-form-fill-in',
    date: '2026-10-16',
    scopes: ['ship'],
    title: { en: 'ISM forms: Fill in button', tr: 'ISM formları: Fill in düğmesi' },
    items: {
      en: [
        'A form now has a Fill in button next to Download and Print: it fills a PDF form in the app, opens a Word or Excel form, or starts a record for a scan.',
        'New Document adds a form to the library; a note now says so, so it is not mistaken for filling in a form.',
        'If a PDF form cannot be checked, Fill in app stays visible and says why. ISM form records are submitted without a password unless the company asks for it everywhere.',
      ],
      tr: [
        'Formun sayfasında Download ve Print yanında Fill in düğmesi var: PDF formu uygulamada doldurur, Word ya da Excel formunu açar ya da tarama için kayıt başlatır.',
        'New Document kütüphaneye form ekler; form doldurmayla karışmasın diye bunu söyleyen bir not eklendi.',
        'PDF form denetlenemezse Fill in app gizlenmez, nedenini söyler. ISM form kayıtları, şirket her yerde istemedikçe parolasız gönderilir.',
      ],
    },
  },
  {
    id: '2026-10-16-payroll-office-payments',
    date: '2026-10-16',
    scopes: ['office', 'ship'],
    title: { en: 'Payroll: payments by the office', tr: 'Payroll: ödemeleri ofis girer' },
    items: {
      en: [
        'The office now records salary and allotment payments, uploads the bank receipt and reconciles them. The vessel sees the payments and receipts read-only.',
        'Payroll items follow the usual order everywhere, including the pay slip: Wages, Extra, Home Allotment, Cash Advance, Slopchest and Balance.',
        'Empty placeholder screens were removed from the Payroll menu.',
      ],
      tr: [
        'Maaş ve allotment ödemelerini artık ofis girer, banka dekontunu yükler ve mutabakatı yapar. Gemi ödemeleri ve dekontları salt okunur görür.',
        'Bordro kalemleri pusula dahil her yerde alışılan sırada: Maaş, Ekstra, Home Allotment, Gemide Avans, Slopchest ve Kalan.',
        'Payroll menüsündeki boş yer tutucu ekranlar kaldırıldı.',
      ],
    },
  },
  {
    id: '2026-10-16-ships-clock',
    date: '2026-10-16',
    scopes: ['office', 'ship'],
    title: { en: "Ship's Clock Log", tr: 'Gemi Saati Kaydı' },
    items: {
      en: [
        "Record every change of the ship's clocks (Crew ▸ Ship's Clock Log): the officer enters it, the Master approves it with a password. Changes can be planned in advance and split over watches.",
        'Hours of rest use the real time that passed: a night when clocks are advanced or retarded is counted correctly, and the 24-hour and 7-day checks are unaffected.',
        'The daily chart marks the clock change, shows a repeated hour as 1st and 2nd, shades the skipped hour and shows 23 h or 25 h days.',
        'The monthly Hours of Rest record and its PDF note each clock change automatically.',
        "Voyage reports and forms take the ship's time from this log; offsets to the minute (for example +05:45) are supported.",
      ],
      tr: [
        'Gemi saatinin her değişikliğini kaydedin (Crew ▸ Ship’s Clock Log): zabit girer, kaptan parolayla onaylar. Değişiklik önceden planlanabilir ve vardiyalara bölünebilir.',
        'Dinlenme saatleri gerçekte geçen süreyle hesaplanır: saatin ileri ya da geri alındığı gece doğru sayılır; 24 saat ve 7 gün denetimleri etkilenmez.',
        'Günlük çizelge saat değişikliğini işaretler, tekrar eden saati 1. ve 2. diye ayırır, atlanan saati taralı gösterir; gün 23 ya da 25 saat diye görünür.',
        'Aylık dinlenme kaydı ve PDF’i her saat değişikliğini kendiliğinden not eder.',
        'Sefer raporları ve formlar gemi saatini bu kayıttan alır; dakika hassasiyetinde farklar (ör. +05:45) desteklenir.',
      ],
    },
  },
  {
    id: '2026-10-15-whats-new',
    date: '2026-10-15',
    scopes: ['office', 'ship'],
    title: { en: 'What’s new in Felixmate', tr: 'Felixmate’da yenilikler' },
    items: {
      en: [
        'This “What’s new” window: open it from the user menu or from Settings ▸ Software to see what changed in each update.',
        'A small “New” mark shows when there are notes you have not read yet.',
      ],
      tr: [
        'Bu “Yenilikler” penceresi: her güncellemede nelerin değiştiğini görmek için kullanıcı menüsünden ya da Ayarlar ▸ Yazılım’dan açın.',
        'Henüz okumadığınız notlar varsa küçük bir “Yeni” işareti görünür.',
      ],
    },
  },
  {
    id: '2026-10-15-pms-library',
    date: '2026-10-15',
    scopes: ['office', 'ship'],
    title: {
      en: 'Company PMS library and Excel import',
      tr: 'Şirket PMS kütüphanesi ve Excel içe aktarma',
    },
    items: {
      en: [
        'A company-wide library of systems, equipment and maintenance jobs, ready-filled from a standard template.',
        'Import and export the library as Excel; a preview shows every row and any errors before anything is saved.',
        'Apply library equipment and jobs to a vessel with a step-by-step wizard. For vessels in service, the Chief Engineer accepts or rejects the request on board.',
        'Equipment now has a Tag field that you can search in the equipment tree.',
      ],
      tr: [
        'Sistemler, ekipmanlar ve bakım işlerinden oluşan, standart şablonla dolu gelen şirket kütüphanesi.',
        'Kütüphaneyi Excel olarak içe ve dışa aktarın; kaydetmeden önce önizleme her satırı ve hataları gösterir.',
        'Kütüphanedeki ekipman ve işleri adım adım sihirbazla gemiye uygulayın. Çalışan gemilerde istek gemide Baş Mühendisin kabulüne gider.',
        'Ekipmanlarda ağaçta aranabilen Tag alanı var.',
      ],
    },
  },
  {
    id: '2026-10-15-ism-form-fill',
    date: '2026-10-15',
    scopes: ['ship'],
    title: { en: 'ISM forms: filling in the app', tr: 'ISM formları: uygulamada doldurma' },
    items: {
      en: [
        'Fill in app now always shows the form fields, and Save to record stays disabled until they are ready.',
        'Retrying an interrupted save reuses the same draft instead of opening a new record number.',
        'The Void window now explains that you are voiding the record.',
      ],
      tr: [
        'Uygulamada doldurma artık form alanlarını her zaman gösteriyor; alanlar hazır olana kadar "Kayda ekle" kapalı.',
        'Yarıda kalan kaydetme yeniden denenince yeni kayıt numarası açılmıyor, aynı taslak kullanılıyor.',
        'İptal (Void) penceresi artık kaydı iptal ettiğinizi açıklıyor.',
      ],
    },
  },
  {
    id: '2026-10-15-crew-payroll',
    date: '2026-10-15',
    scopes: ['office', 'ship'],
    title: { en: 'Crew payroll', tr: 'Mürettebat bordrosu' },
    items: {
      en: [
        'Monthly payroll for each vessel: wages, advances, slop chest and allotments in one place.',
        'A payment book for cash on board and bank transfers, with reconciliation.',
        'Printable pay slips for each crew member, with signature boxes.',
        'Only people you give permission to can see payroll.',
      ],
      tr: [
        'Her gemi için aylık bordro: ücret, avans, slopchest ve allotment tek yerde.',
        'Gemide nakit ve banka havaleleri için ödeme defteri ve mutabakat.',
        'Her gemiadamı için imza kutulu, yazdırılabilir bordro pusulası.',
        'Bordroyu yalnız yetki verdiğiniz kişiler görür.',
      ],
    },
  },
  {
    id: '2026-10-15-voyage-reports',
    date: '2026-10-15',
    scopes: ['office', 'ship'],
    title: { en: 'Voyage reports', tr: 'Sefer raporları' },
    items: {
      en: [
        'Departure and Arrival reports (end of sea passage and berthing), also for departures without a voyage.',
        'Noon report panels and daily reports in port, at anchor and drifting.',
        'Times in local time and UTC, tugs, remaining on board, weather and people on board in every report.',
        'Every report prints as a clean A4 PDF.',
        'Partner feedback applied to the forms: port selection, planned ETD/ETA, position in degrees and minutes, cast off and tugs.',
      ],
      tr: [
        'Kalkış ve varış raporları (deniz seyrinin sonu ve yanaşma); seferi olmayan kalkışlarda da.',
        'Öğle raporu panelleri ile limanda, demirde ve sürüklenmede günlük raporlar.',
        'Her raporda yerel saat ve UTC, römorkörler, gemideki kalan miktarlar, hava ve gemidekiler.',
        'Her rapor düzgün bir A4 PDF olarak yazdırılır.',
        'Ortağın form düzeltmeleri uygulandı: liman seçimi, planlanan ETD/ETA, derece ve dakikayla konum, halat bırakma ve römorkörler.',
      ],
    },
  },
  {
    id: '2026-10-15-ism-hse',
    date: '2026-10-15',
    scopes: ['office', 'ship'],
    title: { en: 'ISM forms, defects and HSE', tr: 'ISM formları, arızalar ve HSE' },
    items: {
      en: [
        'ISM forms work end to end: fill in, submit, approve and keep the history.',
        'Defect report with parts A, B and C, a three-step approval and PDF.',
        'Medical details in accident reports are visible only to people with a separate permission.',
        'HSE reports and port calls can be printed or saved as PDF.',
        'Approval asks for your password only where the rules require it (PMS, rest hours, purchasing, voyage reports); your company can ask for it everywhere.',
      ],
      tr: [
        'ISM formları baştan sona çalışır: doldurma, gönderme, onay ve geçmiş.',
        'A, B ve C bölümlü, üç adımlı onaylı ve PDF’li arıza raporu.',
        'Kaza raporundaki sağlık bilgisini yalnız ayrı yetkisi olanlar görür.',
        'HSE raporları ve liman aramaları yazdırılabilir ya da PDF olarak kaydedilebilir.',
        'Onayda parola yalnız kuralların istediği yerde sorulur (PMS, dinlenme saatleri, satın alma, sefer raporları); şirketiniz her yerde isteyebilir.',
      ],
    },
  },
  {
    id: '2026-10-15-company-nc',
    date: '2026-10-15',
    scopes: ['office'],
    title: { en: 'Company non-conformities', tr: 'Şirket uygunsuzlukları' },
    items: {
      en: [
        'The office can record its own non-conformities (internal audits, management review) with corrective and preventive actions.',
        'Closing needs a root cause, completed actions and a separate approver.',
      ],
      tr: [
        'Ofis kendi bulduğu uygunsuzlukları (iç denetim, yönetimin gözden geçirmesi) düzeltici ve önleyici faaliyetlerle kaydedebilir.',
        'Kapatmak için kök neden, tamamlanmış faaliyetler ve ayrı bir onaylayan gerekir.',
      ],
    },
  },
  {
    id: '2026-10-14-operations',
    date: '2026-10-14',
    scopes: ['office', 'ship'],
    title: { en: 'Operations and safety modules', tr: 'Operasyon ve güvenlik modülleri' },
    items: {
      en: [
        'Ports and distances: choose ports from a world list and see route distances.',
        'HSE reports: non-conformity and PSC, deficiency, near miss and accident.',
        'Daily works plan and daily reports.',
        'Risk assessments and port calls.',
        'Purchasing: bulk requisitions, provisions, prices on stock movements and price history.',
        'An “i” button on every page explains the page with a short animated guide.',
      ],
      tr: [
        'Limanlar ve mesafeler: limanı dünya listesinden seçin, rota mesafelerini görün.',
        'HSE raporları: uygunsuzluk ve PSC, eksiklik, ramak kala ve kaza.',
        'Günlük iş planı ve günlük raporlar.',
        'Risk değerlendirmeleri ve liman aramaları.',
        'Satın alma: toplu talep, kumanya, stok hareketlerinde fiyat ve fiyat geçmişi.',
        'Her sayfadaki “i” düğmesi sayfayı kısa, animasyonlu bir rehberle anlatır.',
      ],
    },
  },
  {
    id: '2026-10-11-foundation',
    date: '2026-10-11',
    scopes: ['office', 'ship'],
    title: { en: 'Earlier updates', tr: 'Önceki güncellemeler' },
    items: {
      en: [
        'Planned maintenance: equipment tree, jobs, running hours, measurements, defects and reports.',
        'Ship certificates, surveys and class, with expiry reminders.',
        'Documents and forms shared between the office and the ship, with read confirmations.',
        'Purchasing, stock, voyages and crew rest hours.',
        'System alerts on board and backup to an external drive.',
      ],
      tr: [
        'Planlı bakım: ekipman ağacı, işler, çalışma saatleri, ölçümler, arızalar ve raporlar.',
        'Gemi sertifikaları, sörveyler ve klas; süre dolumu hatırlatmalarıyla.',
        'Ofisle gemi arasında paylaşılan dokümanlar ve formlar; okundu teyitleriyle.',
        'Satın alma, stok, seferler ve mürettebat dinlenme saatleri.',
        'Gemide sistem uyarıları ve harici diske yedek.',
      ],
    },
  },
];
